/**
 * useAiBuddies — the selectable AI companions (POST /Agent/AgentsList).
 *
 * The endpoint returns the whole roster in one call, so there is no
 * pagination; only load / refresh / retry state is shared here.
 */
import { useCallback, useEffect, useState } from 'react';
import aiBuddyService from '../api/services/aiBuddyService';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import type { ApiError } from '../types/api';
import type { AIBuddy } from '../types/user';

export interface UseAiBuddiesResult {
  buddies: AIBuddy[];
  loading: boolean;
  refreshing: boolean;
  error: string;
  loaded: boolean;
  refresh: () => void;
}

export const useAiBuddies = (): UseAiBuddiesResult => {
  const [buddies, setBuddies] = useState<AIBuddy[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async (isRefresh: boolean) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const response = await aiBuddyService.getAIBuddies();
      setBuddies(response.data ?? []);
    } catch (caught) {
      setError(getApiErrorMessage(caught as ApiError));
      if (!isRefresh) setBuddies([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load(false).catch(() => undefined);
  }, [load]);

  const refresh = useCallback(() => {
    load(true).catch(() => undefined);
  }, [load]);

  return { buddies, loading, refreshing, error, loaded, refresh };
};

export default useAiBuddies;
